import React, { useEffect, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { ReactComponent as CloseIcon } from "../../../../../assets/img/svg/CloseIcon.svg";
import { ReactComponent as Document } from "../../../../../assets/img/svg/DocumentLight.svg";
import AButton from "../form-fields-mui/AButton";
import { getUniqueKey } from "../utils/methods/commonMethods/utilityMethod";
import {
  failureToast,
  successToast,
} from "../utils/methods/toasterFunctions/function";
import { putpost, uploadpost } from "services/utils";
import { endpoint } from "../constant";
import ALoader from "../a-loader";
import { fetchAllPostPromisedData } from "../utils/methods/commonMethods/utilityMethod";
import { ReactComponent as ExcellFile } from "../../../../../assets/img/svg/excel.svg";

const DynamicDocUploadModal = ({
  open,
  setOpen,
  saveHandler,
  values,
  acceptedFileTypes = [],
  maxUploadFileSize, // in Mb
  serviceName,
  uniqueKeyParameter,
  fileUploads = [], // Array of objects with { label, uploadedFileS3Key, uploadedFileName, fieldName, isMandatory }
  modalTitle,
  viewMode = false,
  viewUploadedImage = false,
  propStyle = {},
  isBulkCollection = false,
  customComponent,
  paymentMode,
  fileTypeErrorMsg = "File type not supported!!",
  fileSizeErrorMsg = `The file size exceeds the limit of ${maxUploadFileSize} MB. Please reduce the size of the file to upload successfully`,
  modalProps = {}
}) => {
  const maxFileSize = maxUploadFileSize * 1048576; // converting to bits
  const [fileStates, setFileStates] = useState({});
  const [fileDataStates, setFileDataStates] = useState({});
  const [loading, setLoading] = useState(false);
  const [fileUrls, setFileUrls] = useState({});

  // Initialize file states from fileUploads prop
  useEffect(() => {
    const initialStates = {};
    const initialDataStates = {};
    
    fileUploads.forEach((upload) => {
      initialStates[upload.fieldName] = null;
      initialDataStates[upload.fieldName] = {
        s3Key: upload.uploadedFileS3Key,
        fileName: upload.uploadedFileName,
      };
    });
    
    setFileStates(initialStates);
    setFileDataStates(initialDataStates);
  }, [fileUploads]);

  const headerStyles = {
    fontSize: "16px",
    fontWeight: 600,
    color: "#0F0F0F",
  };

  const infoStyles = {
    fontSize: "14px",
    fontWeight: 400,
    color: "#545454",
  };

  const uploadLabelStyles = {
    ...headerStyles,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    letterSpacing: "1px",
    outline: "none",
    border: "none",
    padding: "10px 16px",
    backgroundColor: "#00B0F5",
    color: "#FFFFFF",
    width: "100%",
    borderRadius: "8px",
    cursor: "pointer",
  };

  const updateLabelStles = {
    ...uploadLabelStyles,
    color: "#00B0F5",
    border: "2px solid #00B0F5",
    backgroundColor: "#FFFFFF",
    padding: "8px",
  };

  const getDataToServer = (fieldName) => ({
    bulk: false,
    fileName: null,
    docType: "pdf",
    keyValue: `${uniqueKeyParameter}_${fieldName}_${getUniqueKey()}`,
    serviceName: serviceName,
  });

  function isValidFileName(fileName) {
    const nameArr = fileName?.split(".");
    nameArr.pop();
    const baseName = nameArr.join(".");
    return /^[a-zA-Z0-9 !\-_.\*'()]+$/.test(baseName);
  }

  const showFile = (dataFile, targetFile, fieldName) => {
    try {
      const fileSelected = dataFile || targetFile;

      if (!isValidFileName(fileSelected?.name)) {
        failureToast("File name contains invalid characters!");
        return;
      }

      const fileType = fileSelected?.name?.split(".")?.pop()?.toLowerCase();
      const uploadConfig = fileUploads.find((u) => u.fieldName === fieldName) || {};
      const allowedTypes = uploadConfig.acceptedFileTypes || acceptedFileTypes || [];
      if (!allowedTypes?.includes(`.${fileType}`)) {
        failureToast(fileTypeErrorMsg);
        return;
      }
      const uploadMaxFileSize = uploadConfig.maxUploadFileSize ? uploadConfig.maxUploadFileSize * 1048576 : maxFileSize;
      if (fileSelected.size > uploadMaxFileSize) {
        failureToast(fileSizeErrorMsg);
        return;
      }
      
      setFileStates((prev) => ({
        ...prev,
        [fieldName]: fileSelected,
      }));
      setLoading(true);
      getFilePath(fileSelected.name, fileSelected, fieldName);
    } catch (e) {
      console.log("Error", e);
      setLoading(false);
    }
  };

  const getFilePath = (name, file, fieldName) => {
    try {
      const formData = {
        ...getDataToServer(fieldName),
        fileName: name,
        docType: name?.split(".")?.pop()?.toLowerCase(),
      };

      putpost(
        endpoint.fetchS3Url.getUrl,
        (data) => {
          if (data?.code === 200 && data.data) {
            saveDataToStorage(data.data, file, fieldName);
          }
        },
        (data) => {
          failureToast(data["message"]);
          setLoading(false);
          setFileStates((prev) => ({
            ...prev,
            [fieldName]: null,
          }));
        },
        formData,
        "post"
      );
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const saveDataToStorage = (res, file, fieldName) => {
    try {
      const formData = new FormData();
      formData.append("File", file);

      uploadpost(
        res.storage,
        (data) => {
          setFileDataStates((prev) => ({
            ...prev,
            [fieldName]: {
              s3Key: res.key,
              fileName: file.name,
              processId: res.processId,
              docType: file.name?.split('.')?.pop()?.toLowerCase(),
            },
          }));
          successToast("File uploaded successfully");
          setLoading(false);
        },
        (data) => {
          failureToast("File upload failed, please try again after sometime.");
          setLoading(false);
        },
        file,
        file?.type,
        file?.name
      );
    } catch (e) {
      failureToast("Unable to upload file, please try after sometime.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewUploadedImage) {
      fileUploads.forEach((upload) => {
        if (fileDataStates[upload.fieldName]?.s3Key) {
          downLoadFile(upload.fieldName, false);
        }
      });
    }
  }, [fileDataStates]);

  const downLoadFile = async (fieldName, download = true) => {
    const currentFileData = fileDataStates[fieldName];
    if (!currentFileData?.s3Key) return;

    const templateUrl = await fetchAllPostPromisedData(
      `${endpoint.fetchS3Url.getDownloadUrl}?key=${currentFileData?.s3Key}`,
      {}
    );
    if (templateUrl?.code === 200 && templateUrl?.data) {
      if (!download) {
        setFileUrls((prev) => ({
          ...prev,
          [fieldName]: templateUrl?.data?.url,
        }));
      } else {
        window.open(`${templateUrl?.data?.url}`);
      }
    } else {
      failureToast("Something went wrong please try after sometime.");
    }
  };

  const FileInput = ({ action, styles, fieldName }) => {
    return (
      <>
        <label htmlFor={`fileInput_${fieldName}`} style={styles}>
          {action}
        </label>
        <input
          onChange={(e) => showFile(null, e.target.files[0], fieldName)}
          type="file"
          hidden
          id={`fileInput_${fieldName}`}
          accept={(fileUploads.find((u) => u.fieldName === fieldName)?.acceptedFileTypes || acceptedFileTypes || []).join(",")}
          disabled={isBulkCollection && !paymentMode}
        />
      </>
    );
  };

  const renderFilePreview = (fieldName) => {
    const name = fileStates[fieldName]?.name || fileDataStates[fieldName]?.fileName || "";
    const ext = (name?.split(".")?.pop() || "").toLowerCase();

    if (viewUploadedImage && fileUrls[fieldName]) {
      return <img src={fileUrls[fieldName]} style={{ height: "225px", width: "225px" }} />;
    }

    if (["pdf", "doc", "docx"].includes(ext)) {
      return <i className="fa fa-file-pdf" style={{ fontSize: 56, color: "#D9534F" }} />;
    }

    if (["xls", "xlsx", "csv"].includes(ext)) {
      return <ExcellFile style={{ width: "56px", height: "53px" }} />;
    }

    return (
      <img
        src={require("../../../../../assets/img/DocumentLarge.jpg")}
      />
    );
  };

  const DisplayImportInput = ({ uploadLabel, fieldName }) => {
    return (
      <>
        <div className="d-flex flex-column align-items-center justify-content-center my-4">
          <Document />
          <span
            className="mt-2"
            style={{ ...infoStyles, color: "#333" }}
          >{`File supported: ${(fileUploads.find((u) => u.fieldName === fieldName)?.acceptedFileTypes || acceptedFileTypes || []).join(", ")}`}</span>
        </div>
        <FileInput
          action={uploadLabel || "Upload"}
          styles={
            isBulkCollection
              ? paymentMode
                ? uploadLabelStyles
                : {
                    ...updateLabelStles,
                    backgroundColor: "#a1aeb7",
                    border: "1px solid #a1aeb7",
                    cursor: "not-allowed",
                    color: "#ffffff",
                  }
              : uploadLabelStyles
          }
          fieldName={fieldName}
        />
      </>
    );
  };

  const DisplayFile = ({ fileName, fieldName, onDownload }) => {
    return (
      <>
        <div className="d-flex flex-column justify-content-center align-items-center my-4">
          {renderFilePreview(fieldName)}
          <span className="mt-2" style={{ ...infoStyles, ...propStyle }}>
            {fileName}
          </span>
        </div>

        {!viewMode && (
          <div className="d-flex flex-column justify-content-center align-items-center">
            <div className="d-flex justify-content-between w-100">
              <FileInput action={"Update"} styles={updateLabelStles} fieldName={fieldName} />

              <AButton
                children={"Download"}
                variant="outline"
                onClick={() => onDownload(fieldName)}
                updatedStyle={{ width: "100%" }}
              />
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Modal size="sm" isOpen={open} {...modalProps}>
      <ModalBody>
        <div className="d-flex justify-content-between mb-1">
          <div style={headerStyles}>{modalTitle}</div>
          <CloseIcon
            style={{ cursor: "pointer" }}
            onClick={() => {
              setFileStates({});
              setOpen(false);
            }}
          />
        </div>
        {isBulkCollection && customComponent}
        <div style={infoStyles}>{`Max file size ${maxUploadFileSize} MB`}</div>

        {loading ? (
          <div style={{ height: "100px" }}>
            <ALoader />
          </div>
        ) : (
          <>
            {fileUploads.map((upload) => {
              const hasFile = fileStates[upload.fieldName] || fileDataStates[upload.fieldName]?.s3Key;
              return (
                <div key={upload.fieldName} className="mb-4">
                  <div style={{ ...infoStyles, marginBottom: "10px", fontWeight: 600 }}>
                    {upload.label}
                  </div>
                  {!hasFile ? (
                    <DisplayImportInput 
                      uploadLabel={upload.uploadLabel}
                      fieldName={upload.fieldName}
                    />
                  ) : (
                    <DisplayFile
                      onDownload={downLoadFile}
                      fileName={fileStates[upload.fieldName]?.name || fileDataStates[upload.fieldName]?.fileName}
                      fieldName={upload.fieldName}
                    />
                  )}
                </div>
              );
            })}
            
            {!viewMode && (
              <AButton
                className="ml-0 mt-3"
                children={"Save"}
                variant="primary_filled"
                onClick={() => {
                  const allFilesData = {};
                  fileUploads.forEach((upload) => {
                    const fd = fileDataStates[upload.fieldName] || {};
                    allFilesData[upload.fieldName] = {
                      s3Key: fd?.s3Key,
                      fileName: fd?.fileName,
                      docType: fd?.docType,
                      processId: fd?.processId,
                      fieldName: upload.fieldName,
                      label: upload.label,
                      isMandatory: upload.isMandatory || false
                    };
                  });
                  saveHandler(allFilesData, setFileStates, setFileDataStates);
                }}
                disabled={fileUploads.some(upload => 
                  (upload.isMandatory !== false) && !fileDataStates[upload.fieldName]?.s3Key
                )}
                updatedStyle={{ width: "100%", marginTop: "10px" }}
              />
            )}
          </>
        )}
        <a style={{ display: "none" }} id="link" download></a>
      </ModalBody>
    </Modal>
  );
};

export default DynamicDocUploadModal;
